import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, PhoneOff, Delete, Mic, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Brand";
import { api, errMsg, useData } from "@/lib/api";
import { fullName, fmtDateTime, TEMP_STYLE } from "@/lib/crm";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const DISPOSITIONS = ["Connected / Interested", "Left Voicemail", "No Answer", "Scheduled Session", "Not Interested"];

export default function Dialer() {
  const location = useLocation();
  const [contacts] = useData("/contacts", []);
  const [contactId, setContactId] = useState(null);
  const [number, setNumber] = useState("");
  const [inCall, setInCall] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [hold, setHold] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [note, setNote] = useState("");
  const [autoAdvance, setAutoAdvance] = useState(true);
  const timer = useRef(null);
  const [calls, setCalls] = useState([]);

  const active = contacts.find((c) => c.id === contactId);
  const idx = contacts.findIndex((c) => c.id === contactId);
  const goNext = () => { if (idx >= 0 && idx < contacts.length - 1) setContactId(contacts[idx + 1].id); };
  const goPrev = () => { if (idx > 0) setContactId(contacts[idx - 1].id); };

  useEffect(() => { const cid = location.state?.contactId; if (cid) setContactId(cid); }, [location.state]);
  useEffect(() => { if (active) setNumber(active.phone || ""); }, [active]);

  const loadCalls = async (id) => { try { const { data } = await api.get(`/calls?contact_id=${id}`); setCalls(data); } catch { setCalls([]); } };
  useEffect(() => { if (contactId) loadCalls(contactId); }, [contactId]);

  useEffect(() => () => clearInterval(timer.current), []);

  const startCall = () => {
    if (!contactId) return toast.error("Pick a lead to dial first");
    setInCall(true); setSeconds(0); setDisposition(""); setNote("");
    timer.current = setInterval(() => setSeconds((s) => (hold ? s : s + 1)), 1000);
  };
  const endCall = async () => {
    clearInterval(timer.current);
    setInCall(false);
    try {
      await api.post("/calls", { contact_id: contactId, disposition: disposition || "No disposition", note, duration: seconds });
      toast.success("Call logged to lead history");
      loadCalls(contactId);
    } catch (e) { toast.error(errMsg(e)); }
    setMuted(false); setHold(false);
    if (autoAdvance) goNext();
  };
  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div>
      <PageHeader title="Power Dialer" subtitle="Dial your leads and log the outcome. Calls are simulated for training (sandbox — no real calls are placed)." />
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <div className="rounded-2xl border bg-[#19082B] p-6 text-white" data-testid="dialer-panel">
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold">Call queue</label>
              {contacts.length > 0 && <span className="text-xs text-white/50" data-testid="dialer-queue-position">{idx >= 0 ? idx + 1 : "–"} / {contacts.length}</span>}
            </div>
            <Select value={contactId || ""} onValueChange={setContactId}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="dialer-lead-select"><SelectValue placeholder="Choose a lead to dial" /></SelectTrigger>
              <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{fullName(c)} — {c.phone || "no number"}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white" onClick={goPrev} disabled={idx <= 0 || inCall} data-testid="dialer-prev-btn"><ChevronLeft className="h-4 w-4 mr-1" />Prev</Button>
              <Button type="button" variant="outline" className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/15 hover:text-white" onClick={goNext} disabled={idx < 0 || idx >= contacts.length - 1 || inCall} data-testid="dialer-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)} className="accent-[#D4AF37]" data-testid="dialer-autoadvance" /> Auto-advance to next lead after each call
            </label>
          </div>
          <div className="rounded-xl bg-black/30 px-4 py-3 text-center mb-4">
            <div className="font-mono text-2xl tracking-wider" data-testid="dialer-number">{number || "—"}</div>
            {inCall && <div className="text-xs text-[#D4AF37] mt-1" data-testid="dialer-timer">{hold ? "On hold" : "Connected"} · {mmss(seconds)}</div>}
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="dialer-keypad">
            {KEYS.map((k) => (
              <button key={k} type="button" onClick={() => setNumber((n) => n + k)} className="rounded-xl bg-white/10 py-3 text-lg font-semibold hover:bg-white/20 active:scale-95 transition" data-testid={`dialer-key-${k}`}>{k}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setNumber((n) => n.slice(0, -1))} className="rounded-xl bg-white/10 p-3 hover:bg-white/20" data-testid="dialer-backspace"><Delete className="h-5 w-5" /></button>
            {!inCall ? (
              <Button onClick={startCall} className="flex-1 bg-[#059669] hover:bg-[#047857]" data-testid="dialer-call-btn"><Phone className="h-4 w-4 mr-1.5" />Call</Button>
            ) : (
              <>
                <button type="button" onClick={() => setMuted((m) => !m)} className={`rounded-xl p-3 ${muted ? "bg-[#D4AF37] text-[#19082B]" : "bg-white/10"}`} data-testid="dialer-mute-btn"><Mic className="h-5 w-5" /></button>
                <button type="button" onClick={() => setHold((h) => !h)} className={`rounded-xl p-3 ${hold ? "bg-[#D4AF37] text-[#19082B]" : "bg-white/10"}`} data-testid="dialer-hold-btn"><Pause className="h-5 w-5" /></button>
                <Button onClick={endCall} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="dialer-end-btn"><PhoneOff className="h-4 w-4 mr-1.5" />End</Button>
              </>
            )}
          </div>
          {inCall && (
            <div className="mt-4 space-y-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold">Call outcome</label>
              <Select value={disposition} onValueChange={setDisposition}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="dialer-disposition-select"><SelectValue placeholder="Select disposition" /></SelectTrigger>
                <SelectContent>{DISPOSITIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Call notes…" className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="dialer-note-input" />
            </div>
          )}
        </div>

        <div className="rounded-2xl border bg-white p-5">
          <div className="font-display text-lg font-bold text-[#19082B] mb-3 flex items-center gap-2">{active ? `Call history · ${fullName(active)}` : "Call history"}{active?.temperature && <span className={`rounded-full border px-1.5 text-[9px] font-bold ${TEMP_STYLE[active.temperature]}`} data-testid="dialer-temp">{active.temperature}</span>}</div>
          {!active && <p className="text-sm text-stone-500">Select a lead to see their call history.</p>}
          {active && !calls.length && <p className="text-sm text-stone-500" data-testid="dialer-history-empty">No calls logged yet.</p>}
          <ul className="space-y-2" data-testid="dialer-history">
            {calls.map((c) => (
              <li key={c.id} className="rounded-xl border p-3" data-testid={`call-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm text-[#19082B]">{c.disposition}</span>
                  <span className="text-xs text-stone-400">{fmtDateTime(c.created_at)}</span>
                </div>
                <div className="text-xs text-stone-500">Duration {Math.floor((c.duration || 0) / 60)}m {(c.duration || 0) % 60}s</div>
                {c.note && <div className="mt-1 text-sm text-stone-600">{c.note}</div>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
